import React, { useEffect, useState } from "react"

import { useStaticQuery, graphql } from "gatsby"
import Image from "gatsby-plugin-sanity-image"
import PropTypes from "prop-types"
import { GoogleReCaptchaProvider } from "react-google-recaptcha-v3"

import * as styles from "./story-item.module.scss"
import { isLoggedIn } from "../../../helpers/auth"
import DarkColor from "../../../helpers/dark-color"
import { dateString } from "../../../helpers/portable-text-mapping"
import PortabletextToPlain from "../../../helpers/portabletext-to-plain"
import Button from "../../atoms/button"
import CopyContainer from "../../atoms/copy-container"
import Grid from "../../atoms/grid"
import Grouping from "../../atoms/grouping"
import Heading from "../../atoms/heading"
import Icon from "../../atoms/icon"
import StickyIntro from "../../atoms/sticky-intro"
import Tag from "../../atoms/tag"
import Title from "../../atoms/title"
import Wrapper from "../../atoms/wrapper"
import RepeaterItem from "../repeater-item"
import Social from "../social"
import SubscribeFirst from "../subscribe-first"

const StoryItem = ({ content, title, subscribe }) => {
  const { site, allSanityDictionary } = useStaticQuery(graphql`
    {
      site {
        siteMetadata {
          siteUrl
        }
      }
      allSanityDictionary {
        nodes {
          key {
            current
          }
          value
        }
      }
    }
  `)
  const [subscribed, setSubscribed] = useState(
    (content.web_article_type === "editorial" && content.partner) ||
      content.web_article_type === "newsroom"
  )
  const [listening, setListening] = useState(false)
  let listen = null
  let utterance = null
  let voice = null
  const editorialColor = () => {
    if (content.editorial_color)
      return content.editorial_color.replaceAll("-", "")
    if (content.sport && content.sport.length > 0)
      return content.sport[0].color?.replaceAll("-", "")
    return null
  }

  if (!content.type) {
    listen = () => {
      utterance = new window.SpeechSynthesisUtterance(
        PortabletextToPlain(content._rawContent)
      )
      voice = window.speechSynthesis
        ?.getVoices()
        .find(
          (v) =>
            v.name.includes("Samantha") ||
            v.name.includes("Zira") ||
            (v.lang.includes("en") && v.name.includes("Female"))
        )
      utterance.voice = voice

      utterance.onend = () => {
        setListening(false)
      }

      if (listening) {
        setListening(false)
        window.speechSynthesis?.cancel()
      } else {
        setListening(true)
        window.speechSynthesis?.cancel()
        window.speechSynthesis?.speak(utterance)
      }
    }
  }

  useEffect(() => {
    window.speechSynthesis.onvoiceschanged = () => {
      voice = window.speechSynthesis
        ?.getVoices()
        .find(
          (v) =>
            v.name.includes("Samantha") ||
            v.name.includes("Zira") ||
            (v.lang.includes("en") && v.name.includes("Female"))
        )
    }
    setSubscribed(
      !!localStorage.getItem(
        `subscribed_${content.vertical?.slug.current || "daily-sports"}`
      ) ||
        (content.web_article_type === "editorial" && content.partner) ||
        content.web_article_type === "newsroom" ||
        isLoggedIn()
    )
    return () => window.speechSynthesis?.cancel()
  }, [])

  const trackSubscribed = () => {
    localStorage.setItem(
      `subscribed_${content.vertical?.slug.current || "daily-sports"}`,
      true
    )
    setSubscribed(true)
  }

  const sports = () => {
    if (content.sport === null) return []
    return Array.isArray(content.sport) ? content.sport : [content.sport]
  }

  const tags = () => (
    <Grouping className={styles.tags}>
      {content.vertical ? (
        <Tag
          light={
            content.web_article_type === "editorial" &&
            DarkColor(editorialColor())
          }
          url={`/${content.vertical.slug.current}`}
        >
          {content.vertical.name}
        </Tag>
      ) : null}
      {sports().length === 0 ? (
        <Tag
          light={
            content.web_article_type === "editorial" &&
            DarkColor(editorialColor())
          }
          url={
            content.web_article_type === "newsroom"
              ? "/newsroom"
              : "/sport/general"
          }
        >
          {content.web_article_type === "newsroom"
            ? allSanityDictionary.nodes.find(
                (item) => item.key.current === "newsroom"
              )?.value
            : allSanityDictionary.nodes.find(
                (item) => item.key.current === "general"
              )?.value}
        </Tag>
      ) : (
        sports().map((sport) => (
          <Tag
            light={
              content.web_article_type === "editorial" &&
              DarkColor(editorialColor())
            }
            key={sport.slug.current}
            url={`/sport/${sport.slug.current}`}
          >
            {sport.name}
          </Tag>
        ))
      )}
    </Grouping>
  )

  const image = (imageRef) => {
    if (imageRef) return imageRef.image
    if (content.type) return content.featured_image
    return content.featured_image?.image
  }

  const partner = (partnerContent, partnerUrl) =>
    partnerContent ? (
      <div>
        <Tag url={partnerUrl || partnerContent.url} partner>
          {partnerContent.prefix ||
            allSanityDictionary.nodes.find(
              (item) => item.key.current === "partner_feature"
            )?.value}
          {partnerContent.logo ? (
            <Image
              {...partnerContent.logo}
              width={100}
              alt={partnerContent.name}
            />
          ) : (
            <> {partnerContent.name}</>
          )}
        </Tag>
      </div>
    ) : (
      ""
    )

  const intro = (
    <StickyIntro>
      {partner(content.partner, content.partner_url)}
      <Heading>{title}</Heading>
      {content.date ? (
        <Heading level="6" semantic={false}>
          {dateString(content.date)}
        </Heading>
      ) : (
        ""
      )}
      <div>{content.abstract}</div>
      {content.web_article_type !== "editorial" ? tags() : ""}
      <div className={styles.actions}>
        <Social
          shareLink={`${site.siteMetadata.siteUrl}/${
            content.type
              ? `${content.type}/${
                  sports().length === 0 ? "general" : content.sport.slug.current
                }`
              : `${content.web_article_type}/${content.slug.current}`
          }
        `}
          shareTitle={title}
          light={
            content.web_article_type === "editorial" &&
            DarkColor(editorialColor())
          }
          story
        />
        {!content.type && subscribed ? (
          <div>
            <Button
              variant={
                content.web_article_type === "editorial" &&
                editorialColor() === "black"
                  ? "tertiary"
                  : "primary"
              }
              pressEvent={listen}
            >
              {!listening ? (
                <>
                  {
                    allSanityDictionary.nodes.find(
                      (item) => item.key.current === "listen_article"
                    )?.value
                  }{" "}
                  <Icon icon="play_arrow" />
                </>
              ) : (
                <>
                  {
                    allSanityDictionary.nodes.find(
                      (item) => item.key.current === "listen_article_stop"
                    )?.value
                  }{" "}
                  <Icon icon="stop" />
                </>
              )}
            </Button>
          </div>
        ) : (
          ""
        )}
      </div>
    </StickyIntro>
  )

  const featuredImage = (imageRef) =>
    image(imageRef) ? (
      <div>
        <Image
          {...image(imageRef)}
          className={styles.image}
          width={600}
          alt={imageRef?.alt || content.featured_image?.alt || title}
        />
        {imageRef?.caption || content.featured_image?.caption ? (
          <Heading level="6" semantic={false}>
            {imageRef?.caption || content.featured_image.caption}
          </Heading>
        ) : (
          ""
        )}
      </div>
    ) : (
      ""
    )

  const body = (
    <div
      className={`${styles.storyWrap} ${
        !subscribed ? styles.storyWrap__blocked : ""
      } ${
        content.web_article_type === "editorial"
          ? styles.storyWrap__editorial
          : ""
      }`}
    >
      {content.type ? (
        <RepeaterItem className={styles.story} content={content} />
      ) : (
        <CopyContainer
          className={`${styles.story} ${
            !subscribed ? styles.story__notSubscribed : ""
          }`}
          ads
          content={content.is_ss_se ? null : content._rawContent}
        >
          {content.is_ss_se ? (
            <>
              <Heading level={2} html={content.title} />
              <CopyContainer content={content._rawContent} />
              {content.additional_article_sections.map((section, index) =>
                section.visible.includes("website") ? (
                  <>
                    {partner(section.partner, section.partner_url)}
                    <Heading level={2} html={section.title} />
                    {featuredImage(section.featured_image)}
                    <CopyContainer
                      ads={index % 2 === 0}
                      content={section._rawContent}
                    />
                  </>
                ) : (
                  ""
                )
              )}
            </>
          ) : (
            ""
          )}
        </CopyContainer>
      )}
      {subscribed ? (
        ""
      ) : (
        <div
          className={`${styles.subscribe} ${
            styles[`bottom__${content.vertical?.color || "cobalt"}`]
          }`}
        >
          <div className={styles.subscribeWrapper}>
            <GoogleReCaptchaProvider
              reCaptchaKey={process.env.GATSBY_RECAPTCHA_KEY}
            >
              <SubscribeFirst
                changeStep={trackSubscribed}
                gating
                illustration={subscribe.illustration.default?.image}
                data={{
                  ...subscribe,
                  heading: {
                    default: subscribe.gating.title,
                  },
                  description: {
                    _rawDefault: subscribe.gating._rawCopy,
                  },
                  ctas: {
                    cta: allSanityDictionary.nodes.find(
                      (item) => item.key.current === "subscribe_gated"
                    )?.value,
                  },
                }}
              />
            </GoogleReCaptchaProvider>
          </div>
          <div className={styles.subscribeBottom}>
            <button
              className={styles.subscribeAlready}
              type="button"
              aria-label="subscribe"
              onClick={trackSubscribed}
              onKeyPress={trackSubscribed}
            >
              {
                allSanityDictionary.nodes.find(
                  (item) => item.key.current === "subscribe_already"
                )?.value
              }
            </button>
          </div>
        </div>
      )}
    </div>
  )

  return content.web_article_type === "editorial" ? (
    <>
      <Title color={editorialColor()}>
        <Grid col={2}>
          {intro}
          {featuredImage()}
        </Grid>
      </Title>
      <Wrapper container>
        <Grid col={1}>{body}</Grid>
      </Wrapper>
    </>
  ) : (
    <Wrapper container>
      <Grid col={2}>
        {intro}
        <div>
          {featuredImage()}
          {body}
        </div>
      </Grid>
    </Wrapper>
  )
}

export default StoryItem

StoryItem.propTypes = {
  content: PropTypes.object.isRequired,
  title: PropTypes.string.isRequired,
  subscribe: PropTypes.object.isRequired,
}
